import type { ApiError } from '../types/api';

export class ApiClient {
  private baseUrl: string;
  private tenant: string;

  constructor(tenant: string) {
    this.tenant = tenant;
    // In development, Vite proxy handles /api routes
    // In production, use VITE_API_URL env var or same origin
    // @ts-ignore
    const apiBase = import.meta.env.VITE_API_URL || '';
    this.baseUrl = apiBase
      ? `${apiBase}/${tenant}/api`
      : `/${tenant}/api`;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    // Get token from localStorage
    const token = localStorage.getItem('auth_token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers as Record<string, string>,
    };

    // Add Authorization header if token exists
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      const error: ApiError = await response.json().catch(() => ({
        error: 'An unexpected error occurred',
        code: 'UNKNOWN_ERROR',
      }));
      throw new ApiClientError(response.status, error.error, error.code);
    }

    return response.json();
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

export class ApiClientError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

let apiClient: ApiClient | null = null;

export function getApiClient(tenant: string): ApiClient {
  // @ts-ignore
  if (!apiClient || (apiClient as { tenant?: string }).tenant !== tenant) {
    apiClient = new ApiClient(tenant);
  }
  return apiClient;
}
